import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';

interface FavoriteButtonProps {
  /** Type de favori du Core : product, service, business, accommodation, activity, professional. */
  entityType: string;
  entityId: string | number;
  label?: string | null;
  /** Métadonnées libres (image, ville, prix, devise, boutique…). */
  metadata?: Record<string, unknown> | null;
  /** Classes additionnelles (marge, etc.). */
  className?: string;
}

/**
 * Bouton « cœur » réutilisable : un seul clic enregistre ou retire un élément
 * des favoris du client connecté. Utilisé sur les fiches publiques (produit,
 * service) comme dans le chat IA. Si le visiteur n'est pas connecté, il est
 * invité à se connecter.
 */
export default function FavoriteButton({
  entityType,
  entityId,
  label,
  metadata,
  className = '',
}: FavoriteButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isFav, toggle } = useFavorites();
  const [busy, setBusy] = useState(false);

  const active = isFav(entityType, entityId);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate('/login-client');
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      await toggle({
        userId: user.id,
        entityType,
        entityId,
        label: label ?? null,
        metadata: metadata ?? null,
      });
    } catch {
      /* best-effort : on ne bloque jamais l'utilisateur */
    } finally {
      setBusy(false);
    }
  }, [user, busy, toggle, entityType, entityId, label, metadata, navigate]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-pressed={active}
      title={active ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 ${
        active
          ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
          : 'border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100'
      } ${className}`}
    >
      <i className={`${active ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-base leading-none`}></i>
      <span className="hidden sm:inline">{active ? 'Enregistré' : 'Favori'}</span>
    </button>
  );
}